import { useSyncExternalStore } from 'react';
import { guideUserScope, readEnabled, readSeen, writeEnabled, writeSeen } from './guideStorage';

/**
 * Stan przewodnika po zmianach wspólny dla całej aplikacji (21.10.2026):
 * nakładka w układzie panelu, pozycje w menu użytkownika i przycisk
 * „Przewodnik” na ekranie przełączników rozmawiają przez ten magazyn —
 * menu zamyka się po kliknięciu, a otwarty przewodnik ma zostać.
 *
 * Włączony/wyłączony i „obejrzane” są pamiętane per konto (`guideStorage`);
 * „Nie teraz” przy propozycji — tylko do końca sesji karty.
 */

export type HighlightRequest = { flagKey: string; step: number; token: number };

export type GuideState = {
  /** Konto, dla którego wczytano pamięć. */
  scope: string;
  /** Przewodnik włączony w menu użytkownika (domyślnie tak). */
  enabled: boolean;
  /** Przewodnik w panelu; `null` = panel zamknięty. */
  openKey: string | null;
  /** Krok (od 0). */
  step: number;
  /** Panel zwinięty do paska. */
  collapsed: boolean;
  /** Okno „Co nowego”. */
  whatsNewOpen: boolean;
  /** Duży podgląd slajdów: przewodnik i slajd (od 0). */
  viewer: { flagKey: string; step: number } | null;
  /** Obejrzane przewodniki (klucze przełączników). */
  seen: ReadonlySet<string>;
  /** Propozycje odłożone „Nie teraz” w tej sesji. */
  dismissed: ReadonlySet<string>;
  /** Prośba o podświetlenie elementu kroku — nowa wartość = nowa prośba. */
  highlight: HighlightRequest | null;
};

const initial = (scope: string): GuideState => ({
  scope,
  enabled: readEnabled(scope),
  openKey: null,
  step: 0,
  collapsed: false,
  whatsNewOpen: false,
  viewer: null,
  seen: readSeen(scope),
  dismissed: new Set(),
  highlight: null,
});

let state: GuideState | null = null;
let token = 0;
const listeners = new Set<() => void>();

function current(): GuideState {
  if (!state) state = initial(guideUserScope());
  return state;
}

function set(patch: Partial<GuideState>) {
  state = { ...current(), ...patch };
  listeners.forEach((l) => l());
}

export function getGuideState(): GuideState {
  return current();
}

export function subscribeGuide(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Hak dla komponentów — przelicza widok po każdej zmianie. */
export function useGuideState(): GuideState {
  return useSyncExternalStore(subscribeGuide, getGuideState);
}

/**
 * Inne konto w tej samej karcie (wylogowanie i logowanie) — pamięć od nowa,
 * panel zamknięty. Bez zmiany konta nic się nie dzieje.
 */
export function syncGuideUser(scope = guideUserScope()) {
  if (current().scope === scope) return;
  state = initial(scope);
  listeners.forEach((l) => l());
}

function markSeen(flagKey: string) {
  const s = current();
  if (s.seen.has(flagKey)) return s.seen;
  const seen = new Set(s.seen);
  seen.add(flagKey);
  writeSeen(s.scope, seen);
  return seen;
}

/**
 * Otwórz przewodnik na kroku. Jawne otwarcie włącza przewodnik (kto go
 * wyłączył, a teraz klika „Pokaż przewodnik”, chce go zobaczyć)
 * i zapisuje „obejrzane”.
 */
export function openGuide(flagKey: string, step = 0) {
  const s = current();
  if (!s.enabled) writeEnabled(s.scope, true);
  token += 1;
  set({
    enabled: true,
    openKey: flagKey,
    step: Math.max(0, step),
    collapsed: false,
    whatsNewOpen: false,
    seen: markSeen(flagKey),
    highlight: { flagKey, step: Math.max(0, step), token },
  });
}

export function closeGuide() {
  set({ openKey: null, step: 0, collapsed: false, highlight: null });
}

/** Krok w otwartym przewodniku; `highlight` — od razu podświetl jego element. */
export function goToStep(step: number, highlight = true) {
  const s = current();
  if (!s.openKey) return;
  token += 1;
  set({ step: Math.max(0, step), highlight: highlight ? { flagKey: s.openKey, step: Math.max(0, step), token } : null });
}

/** „Pokaż na ekranie” przy bieżącym kroku. */
export function requestHighlight() {
  const s = current();
  if (!s.openKey) return;
  token += 1;
  set({ highlight: { flagKey: s.openKey, step: s.step, token } });
}

export function clearHighlight() {
  set({ highlight: null });
}

export function setCollapsed(collapsed: boolean) {
  set({ collapsed });
}

/** Włącz/wyłącz w menu użytkownika; wyłączenie zamyka panel i propozycje. */
export function setGuideEnabled(enabled: boolean) {
  const s = current();
  writeEnabled(s.scope, enabled);
  set(enabled ? { enabled } : { enabled, openKey: null, collapsed: false, highlight: null, viewer: null });
}

export function openWhatsNew() {
  set({ whatsNewOpen: true });
}

export function closeWhatsNew() {
  set({ whatsNewOpen: false });
}

/** „Nie teraz” — propozycja tego przewodnika wraca dopiero w nowej sesji. */
export function dismissProposal(flagKey: string) {
  const dismissed = new Set(current().dismissed);
  dismissed.add(flagKey);
  set({ dismissed });
}

/** „Nie pokazuj” — jak obejrzany (bez otwierania). */
export function markGuideSeen(flagKey: string) {
  set({ seen: markSeen(flagKey) });
}

export function openViewer(flagKey: string, step: number) {
  set({ viewer: { flagKey, step: Math.max(0, step) } });
}

export function closeViewer() {
  set({ viewer: null });
}

/** Tylko dla testów: stan od nowa. */
export function resetGuideStore() {
  state = null;
  token = 0;
  listeners.clear();
}
